import {ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, OnInit, output, OutputEmitterRef, signal, Signal, WritableSignal} from '@angular/core';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {ImportService} from '../../../../services/import/import.service';
import {VaultQuery} from '../../../../queries/vault.query';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {map, startWith} from 'rxjs/operators';
import {Vault} from '../../../../api/models';
import {BasicSubscribableComponent} from '../../../dummy-components/basic-subscribable-component';
import {ICONS} from '../../../../constants/icons/icons.constants';
import {DocumentFilenameComponent} from '../../../document-filename/document-filename.component';
import {DropImportDirective} from '../../../../directives/drop-import.directive';
import {AsyncPipe} from '@angular/common';
import {MagnetService} from '../../../../services/magnet/magnet.service';
import {toSignal} from '@angular/core/rxjs-interop';
import {ImportLocation} from '../../../../models/import/import-location.model';
import {SameFileDetectedEnum} from '../../../../enums/same-file-detected.enum';
import {ImportLocationEnum} from '../../../../enums/import-location.enum';
import {Magnet} from '../../../../bfa-api/models/magnet';
import {ImportDialogData} from '../../../../models/import/import-dialog-data.model';
import {UploadFormData} from '../../../../models/import/upload-form-data.model';
import {AutocompleteInputComponent} from '../../../form-elements/autocomplete-input/autocomplete-input.component';
import {AutocompleteSuggestion} from '../../../../models/forms/autocomplete-suggestion.model';
import {ApiVaultIconUrlPipe} from '../../../../pipes/api-icon-url/api-vault-icon-url.pipe';
import {SelectComponent} from '../../../form-elements/select/select.component';
import {SelectOption} from '../../../../models/forms/select-option.model';

@Component({
    selector: 'app-import-form-dialog',
    templateUrl: './import-form-dialog.component.html',
    styleUrls: ['./import-form-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ReactiveFormsModule,
        DocumentFilenameComponent,
        DropImportDirective,
        TranslateModule,
        AsyncPipe,
        AutocompleteInputComponent,
        ApiVaultIconUrlPipe,
        SelectComponent,
    ]
})
export class ImportFormDialogComponent extends BasicSubscribableComponent implements OnInit {
    public data: InputSignal<ImportDialogData> = input<ImportDialogData>({});
    public formDone: OutputEmitterRef<UploadFormData> = output<UploadFormData>();
    public formValidation: OutputEmitterRef<boolean> = output<boolean>();
    public closed: OutputEmitterRef<void> = output<void>();
    protected files$: BehaviorSubject<Array<File>>;
    protected autocompleteVaults: Signal<Array<AutocompleteSuggestion>>;
    protected selectOptionsImportLocations: Signal<Array<SelectOption>>;
    protected form: FormGroup;
    protected selectedVault$: BehaviorSubject<Vault | undefined>;
    protected selectedVaultIconUrl: WritableSignal<string | undefined> = signal(undefined);
    protected readonly ICONS = ICONS;
    protected readonly selectOptionsSameFileSettings: WritableSignal<Array<SelectOption>>;
    private readonly locations: Array<ImportLocation>;
    private readonly defaultTarget: ImportLocation;
    private readonly chosenVault: WritableSignal<Vault | undefined> = signal(undefined);
    private readonly availableMagnet: Signal<Magnet | undefined>;
    private readonly formBuilder = inject(FormBuilder);
    private readonly importService = inject(ImportService);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly magnetService = inject(MagnetService);
    private readonly translateService = inject(TranslateService);

    public constructor() {
        super();

        this.files$ = this.importService.files$;
        this.selectedVault$ = new BehaviorSubject<Vault | undefined>(undefined);

        this.defaultTarget = {
            id: ImportLocationEnum.Documents,
            name: this.translateService.instant('DOCUMENTS')
        };

        this.locations = [
            {
                id: ImportLocationEnum.InProgress,
                name: this.translateService.instant('IN_PROGRESS')
            },
            this.defaultTarget
        ];

        this.form = this.formBuilder.group({
            vault: new FormControl<string | null>('',
                [
                    Validators.required,
                    (control: AbstractControl<string | null>): ValidationErrors | null => this.validateKnownVault(control)
                ]),
            target: new FormControl<string | null>('', [Validators.required]),
            sameFileDetected: new FormControl<string | null>(SameFileDetectedEnum.dontimport, Validators.required),
        });

        const selectedMagnetSignal = this.magnetService.getSelectedMagnetSignal();
        
        this.availableMagnet = computed((): Magnet | undefined => {
            const target = this.data().target;
            const magnet = target && 'magnetId' in target ? target : selectedMagnetSignal();

            return magnet && magnet.magnetVaultId === this.chosenVault()?.id ? magnet : undefined;
        });

        this.selectOptionsImportLocations = computed((): Array<SelectOption> => {
            const locationOptions: Array<SelectOption> = this.locations.map(location => {
                return {
                    svgIcon: location.id === ImportLocationEnum.InProgress ? ICONS.INPROGRESS : ICONS.FOLDER,
                    label: location.name,
                    value: location.id,
                };
            });
            const magnet = this.availableMagnet();

            if (!magnet) {
                return locationOptions;
            }

            return [
                ...locationOptions,
                {
                    svgIcon: ICONS.FOLDER,
                    label: magnet.magnetName,
                    value: magnet.magnetId,
                }
            ];
        });

        this.selectOptionsSameFileSettings = signal([
            {
                value: SameFileDetectedEnum.dontimport as string,
                label: this.translateService.instant('IMPORT.COLLISION_SETTINGS_OPTION.DONTIMPORT'),
            },
            {
                value: SameFileDetectedEnum.overwrite as string,
                label: this.translateService.instant('IMPORT.COLLISION_SETTINGS_OPTION.OVERWRITE'),
            },
            {
                value: SameFileDetectedEnum.extend as string,
                label: this.translateService.instant('IMPORT.COLLISION_SETTINGS_OPTION.EXTEND'),
            }
        ]);

        this.autocompleteVaults = toSignal(
            combineLatest([
                this.vaultQuery.vaults$,
                this.form.controls.vault.valueChanges.pipe(startWith(''))
            ])
                .pipe(map(([vaults, filterWord]: [Array<Vault>, string | null]) => {
                    return vaults.filter(vault => {
                            if (filterWord) {
                                return vault.name.toLowerCase()
                                    .includes(filterWord.toLowerCase());
                            }
                            return true;
                        })
                        .map(vault => {
                            return {
                                id: vault.id,
                                text: vault.name,
                                iconUrl: vault.id,
                                fallbackIcon: ICONS.VAULT,
                            };
                        });
                })), {
                initialValue: []
            });
    }

    public ngOnInit(): void {
        const data = this.data();
        if (data.vaultId) {
            this.selectedVault$.next(this.vaultQuery.getVaultById(data.vaultId));
        } else {
            this.selectedVault$.next(this.vaultQuery.getFirstActiveVault());
        }

        const collisionHandling = data.collisionSetting || 'dontimport';
        const selectedVault = this.selectedVault$.getValue();
        this.chosenVault.set(selectedVault);
        this.selectedVaultIconUrl.set(selectedVault?.id);

        this.form.patchValue({
            vault: selectedVault?.name ?? '',
            target: this.getDefaultTargetId(),
            sameFileDetected: collisionHandling,
        });
        this.formValidation.emit(this.form.valid);

        if ('files' in data && data.files && data.files.length > 0) {
            if (!this.importService.addFiles(data.files)) {
                //ExpressionChangedAfterItHasBeenCheckedError prevention
                setTimeout(() => {
                    this.closed.emit();
                }, 1);
            }
        }

        this.subscriptions.add(this.form.controls.vault.valueChanges.subscribe((vaultName: string | null) => {
            const vault = vaultName ? this.findVaultByName(vaultName) : undefined;

            this.chosenVault.set(vault);
            this.selectedVaultIconUrl.set(vault?.id);

            if (!vault) {
                this.form.controls.target.disable();

                return;
            }

            this.form.controls.target.enable();

            if (vault.id !== this.selectedVault$.getValue()?.id) {
                this.form.controls.target.reset(this.defaultTarget.id);
                return;
            }

            this.form.controls.target.reset(this.getDefaultTargetId());
        }));

        this.subscriptions.add(this.form.valueChanges.subscribe(() => {
            this.formValidation.emit(this.form.valid);
        }));

        if (data.documentId) {
            this.form.controls.vault.disable();
            this.form.controls.target.disable();
            this.form.controls.sameFileDetected.disable();
        }
    }

    public uploadFiles(event: Event): void {
        const target = event.target as HTMLInputElement;
        const files = this.importService.fileListToArray(target?.files as FileList);
        this.addFiles(files);
    }

    public upload(): void {
        const formValue = this.form.getRawValue();
        const vault = formValue?.vault ? this.findVaultByName(formValue.vault) : undefined;

        if (!vault) {
            return;
        }

        this.formDone.emit({
            ...formValue,
            vault
        });
    }

    public addFiles(files: Array<File>): void {
        this.importService.addFiles(files);
        this.formValidation.emit(this.form.valid);
    }

    private getDefaultTargetId(): string {
        const target = this.data().target;

        if (target && 'id' in target) {
            return this.locations.find(location => location.id === target.id)?.id ?? this.defaultTarget.id;
        }

        return this.availableMagnet()?.magnetId ?? this.defaultTarget.id;
    }

    private findVaultByName(vaultName: string): Vault | undefined {
        const selectedVault = this.selectedVault$.getValue();

        if (selectedVault?.name === vaultName) {
            return selectedVault;
        }

        return this.vaultQuery.getAll()
            .find(vault => vault.name === vaultName);
    }

    private validateKnownVault(control: AbstractControl<string | null>): ValidationErrors | null {
        const vault = control.value ? this.findVaultByName(control.value) : undefined;

        if (!control.value || vault) {
            return null;
        }

        return { unknownVault: true };
    }
}
